import { useFixedLocationTypesQuery, useLocationListQuery } from "@graphql/api";
import Dialog from "@hudson/ui/components/Dialogs/Simple";
import Input from "@hudson/ui/components/FormFields/Input";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import Session from "@lib/session";
import useContent from "@lib/use_content";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import TypeIcon from "../TypeIcon";
import Pagination from "./Pagination";

export default function FixedLocationDialog({
  noAutosizing,
  onHide,
  onLocationSelect,
  serviceArea = Session.getBooking().serviceArea || Session.getConfig().serviceArea,
  show,
  locationType,
  locationTypeString,
}: {
  noAutosizing?: boolean;
  onHide: () => void;
  onLocationSelect: (loc: be.Location) => void;
  serviceArea?: number;
  show: boolean;
  locationType: string;
  locationTypeString?: string;
}): JSX.Element | null {
  const { data: cData } = useContent(["beErrNoLocsFoundFixed"]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const searchParams = useSearchParams();
  const [count, setCount] = useState(searchParams?.get("widget") ? 6 : 15);
  const { data, fetchMore, loading, error } = useLocationListQuery({
    variables: {
      locationType,
      page,
      count,
      search,
      serviceAreaId: serviceArea,
    },
  });
  const {
    data: flData,
    loading: flLoading,
    error: flError,
  } = useFixedLocationTypesQuery({
    variables: {
      pudId: Session.getBooking().pudPersonId
        ? parseInt(Session.getBooking().pudPersonId)
        : undefined,
      serviceAreaId: serviceArea,
    },
  });

  useEffect(() => {
    setPage(1);
  }, [search]);

  fetchMore({ variables: { page: page + 1 } });
  fetchMore({ variables: { page: page + 2 } });
  fetchMore({ variables: { page: page + 3 } });

  const title = flData?.fixedLocationTypes?.find(
    (lt: be.LocationType) => lt.type == locationType,
  )?.label;
  const results = data?.locationList?.locations || [];
  let minHeight = "50px";
  if (loading || data?.locationList.pages > 1) {
    minHeight = searchParams?.get("widget") ? "275px" : "450px";
  }

  return (
    <Dialog
      show={show}
      maxWidthClass="max-w-4xl"
      onHide={onHide}
      pagePath={`book/locationsPopup/${locationType}`}
      noAutosizing={noAutosizing}
    >
      <div className="pb-6">
        <h1 className="flex mb-8 text-3xl font-bold tracking-tight text-gray-900 ">
          <TypeIcon typeKey={locationType} type={locationTypeString} className="w-8 h-8 mr-4" />
          {title}
        </h1>
        <>
          {((data?.locationList?.totalItems || 0) > 10 || search) && (
            <div className="relative mx-auto mt-2 rounded-md shadow-sm w-72">
              <Input
                placeholder="Search"
                className="w-full mb-8 -mt-4 "
                onChange={setSearch}
                value={search}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 text-gray-400"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
              </div>
            </div>
          )}

          {flLoading || loading ? (
            <div style={{ minHeight }}>
              <ConcentricLoader
                className="max-w-2xl px-4 py-10 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8"
                waitBeforeShow={250}
              />
            </div>
          ) : results.length == 0 && !loading ? (
            <div style={{ minHeight }} className="flex justify-center text-gray-400">
              <p>{cData?.content.beErrNoLocsFoundFixed}</p>
            </div>
          ) : (
            <div style={{ minHeight }} className="space-y-4 columns-1 xxs:columns-2 sm:columns-3">
              {[...results]
                .sort((a, b) => String(a.displayName).localeCompare(String(b.displayName)))
                .map((loc: be.Location) => (
                  <div
                    key={loc.id}
                    className="cursor-pointer break-inside-avoid-column"
                    onClick={() => {
                      onHide();
                      onLocationSelect(loc, locationType);
                    }}
                  >
                    <p
                      style={{ color: "var(--primary-color)" }}
                      className="m-0 text-sm sm:text-sm hover:underline"
                    >
                      {loc.displayName}
                    </p>
                    <p className="m-0 text-sm font-thin text-gray-400 break-before-avoid">
                      {loc.address}
                    </p>
                    {loc.city && (
                      <p className="m-0 text-sm font-thin text-gray-400 break-before-avoid">
                        {loc.city}
                        {loc.state && `, ${loc.state}`}
                      </p>
                    )}
                  </div>
                ))}
            </div>
          )}
        </>
        {data?.locationList?.pages > 1 && (
          <Pagination
            className="mt-4"
            currentPage={page}
            loading={loading}
            pageItemCount={count}
            totalPages={data?.locationList?.pages || 1}
            totalItems={data?.locationList?.totalItems || 0}
            onPageChange={setPage}
          />
        )}
      </div>
    </Dialog>
  );
}
